
import React, { useState, useEffect, useCallback } from 'react';
import { Menu, ChevronLeft, ChevronRight } from 'lucide-react';
import { AdminView } from '../types';
import { NAVIGATION_ITEMS } from '../constants';
import { useAuth } from '../../context/AuthContext';
import { apiUrl } from '../../utils/api';

interface SidebarProps {
  activeView: AdminView;
  onViewChange: (view: AdminView) => void;
  isOpen: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeView, onViewChange, isOpen, toggleSidebar }) => {
  const { user } = useAuth() as any;
  const normalizedRole = user?.role?.toLowerCase?.() || '';

  const [usagePercent, setUsagePercent] = useState(0);
  const [planName, setPlanName] = useState('Free');

  const fetchUsage = useCallback(async () => {
    try {
      const params = new URLSearchParams({ plan: 'Free' });
      if (user?.organization_id) {
        params.set('org_id', user.organization_id);
      }
      const response = await fetch(apiUrl(`/api/usage?${params.toString()}`));
      if (response.ok) {
        const data = await response.json();
        setUsagePercent(data.percent);
        setPlanName(data.plan || 'Free');
      }
    } catch (err) {
      console.error('Error fetching sidebar usage:', err);
    }
  }, [user?.organization_id]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const visibleItems = NAVIGATION_ITEMS.filter((item) => {
    if (item.id === AdminView.ORGANIZATIONS) {
      return normalizedRole === 'super admin';
    }
    if (item.id === AdminView.USERS_ACCESS || item.id === AdminView.SETTINGS) {
      return normalizedRole !== 'builder';
    }
    return true;
  });
  return (
    <aside
      className={`fixed top-0 left-0 h-full bg-slate-900 text-white z-40 transition-all duration-300 ${isOpen ? 'w-64' : 'w-20'}`}
    >
      <div className="p-4 flex items-center justify-between border-b border-slate-800">
        {isOpen ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-white shadow-lg shadow-blue-500/30">
              E
            </div>
            <span className="font-bold text-lg tracking-tight">EngazeIQ</span>
          </div>
        ) : (
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-white mx-auto">
            E
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="p-1 hover:bg-slate-800 rounded transition-colors"
        >
          {isOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <nav className="mt-4 px-2 space-y-1">
        <button
          onClick={() => window.location.href = '/'}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-slate-400 hover:bg-slate-800 hover:text-white mb-4"
        >
          <span className="text-slate-500">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          </span>
          {isOpen && <span className="font-medium text-sm whitespace-nowrap">Back to Chat</span>}
        </button>
        {visibleItems.map((item: any) => (
          <button
            key={item.id}
            onClick={() => onViewChange(item.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${activeView === item.id
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
          >
            <span className={`${activeView === item.id ? 'text-white' : 'text-slate-500'}`}>
              {item.icon}
            </span>
            {isOpen && <span className="font-medium text-sm whitespace-nowrap">{item.label}</span>}
          </button>
        ))}
      </nav>

      <div className="absolute bottom-4 left-0 w-full px-4">
        {isOpen && (
          <div className="p-3 bg-slate-800 rounded-xl border border-slate-700">
            <p className="text-[10px] uppercase font-bold text-slate-500 mb-2">Plan Usage</p>
            <div className="h-1.5 w-full bg-slate-700 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full transition-all duration-500 ease-out ${
                  usagePercent >= 100
                    ? 'bg-red-500'
                    : usagePercent >= 80
                      ? 'bg-amber-500'
                      : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              ></div>
            </div>
            <p className="text-[11px] text-slate-400">{Math.round(usagePercent)}% of {planName} Plan</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
